import { OpenPanel as BaseOpenPanel } from '@openpanel/web';
import { startReplayRecorder, stopReplayRecorder } from './replay-recorder';
import { getConfig } from 'config/runtime';

// @openpanel/web's loadReplayModule() uses `import("./replay-V6FXES7X.js")`
// which CRA/webpack can't bundle (file isn't in @openpanel/web's package
// `exports` field). The SDK constructor calls this.loadReplayModule()
// synchronously when sessionReplay is enabled, so an instance-level
// monkey-patch is too late. Subclass and override on the prototype so
// the constructor's call hits our vendored module instead.
class OpenPanel extends BaseOpenPanel {
  // eslint-disable-next-line class-methods-use-this
  async loadReplayModule() {
    return { startReplayRecorder, stopReplayRecorder };
  }
}

const parseBool = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  return /^(1|true|yes|on)$/i.test(String(value).trim());
};

const parseRatio = (value, fallback = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1) return fallback;
  return n;
};

let client = null;
let initialized = false;

export function initAnalytics() {
  if (initialized) return client;
  initialized = true;

  const enabled = parseBool(getConfig('REACT_APP_OPENPANEL_ENABLED'), false);
  const clientId = getConfig('REACT_APP_OPENPANEL_CLIENT_ID');

  if (!enabled || !clientId) return null;

  const apiUrl = getConfig('REACT_APP_OPENPANEL_API_URL') || 'https://analytics.getandride.dev/api';
  const replayEnabled = parseBool(getConfig('REACT_APP_OPENPANEL_REPLAY_ENABLED'), false);
  const replaySampleRate = parseRatio(getConfig('REACT_APP_OPENPANEL_REPLAY_SAMPLE_RATE'), 0.1);

  client = new OpenPanel({
    clientId,
    apiUrl,
    trackScreenViews: true,
    trackOutgoingLinks: true,
    trackAttributes: true,
    ...(replayEnabled && {
      sessionReplay: {
        enabled: true,
        sampleRate: replaySampleRate,
        maskAllInputs: true,
        maskAllText: true,
        unmaskTextSelector: 'nav, header, h1, h2, h3, h4, button, [data-replay-safe], .breadcrumb, .menu-item',
        blockSelector: '[data-replay-block], .ktp-preview, .sim-preview, [data-private]',
        blockClass: 'no-replay',
        flushIntervalMs: 10000,
      },
    }),
  });

  return client;
}

const sanitizeProperties = (props = {}) => {
  const out = {};
  Object.entries(props).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    out[key] = value;
  });
  return out;
};

export function identifyUser(profile) {
  if (!client || !profile?.id) return;
  const fullName = profile.name || '';
  const [firstName, ...rest] = fullName.split(' ').filter(Boolean);
  const lastName = rest.join(' ');

  client.identify({
    profileId: String(profile.id),
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    email: profile.email || undefined,
    properties: sanitizeProperties({
      role_id: profile.role_id,
      role_name: profile.role?.name,
      phone: profile.phone,
      status: profile.status,
    }),
  });
}

export function trackEvent(name, properties = {}) {
  if (!client || !name) return;
  client.track(name, sanitizeProperties(properties));
}

export function clearAnalytics() {
  if (!client) return;
  client.clear();
}

export function getAnalyticsClient() {
  return client;
}
